/*
 *
 * Score Report page
 *
 */

import React, { useState, useEffect } from "react";
import { Container, Card, Col, Row, Button } from "react-bootstrap";
import { object, func, string, bool, number, oneOfType } from "prop-types";
import { format } from "date-fns";
import { convertDateTimezone } from "utils/helperFunctions";
import { addDashesToEventID } from "containers/Results/FunctionLib";
import arrowLeft from "resources/images/arrow-left.png";
import ExtendedBadge from "resources/images/dynamic_score_reports/extended-badge.png";
import { trackDynamicScoreReport } from "utils/trackingFunctions";
import { EVENT_DYNAMIC_SCORE_REPORT_VIEW } from "utils/trackingConstants";
import TestInfoSection from "./TestInfoSection";
import ScoreReportNav from "./ScoreReportNav";
import { showInvalidTestWarning, getScoreRangeName } from "../helpers";
import { renderTestReport, renderDownloadButton } from "../renderHelpers";
import WorkStylesReport from "./TestReports/IllustraitReports/WorkStylesReport";

const ScoreReportPage = (props) => {
  const {
    allTests,
    candidateInfo,
    companyAccountId,
    handleBack,
    handleDownloadPdf,
    scheduledBy,
    testEventId,
    testId,
    hideBaseline,
    onGetDynamicWorkStyles,
    workStylesData,
    workStylesLoaded,
    workStylesError,
    isInsightUser,
    isVistaUser,
    fromCandidateProfile,
    onCopyCandidateToJob,
    moveEmailCandidatesPermission,
    featureFlags,
    pipelineStageName,
    isSampleJob,
    userData,
    hsVersion,
    onRedirectToDevelop,
  } = props;
  /* ------------------------------------ States -----------------------------------  */
  const [testInfo, setTestInfo] = useState(
    allTests.get(testId || window.location.query.testId)
  );
  const [showReportNav, setShowReportNav] = useState(false);
  const [tabToDisplay, setTabToDisplay] = useState(null);
  const [initialScoreRange, setInitialScoreRange] = useState(null);

  // for selecting job families in the EPP score report
  const [selectedScoreRange, setSelectedScoreRange] = useState(null);

  /* ------------------------------------ useEffect Hooks -----------------------------------  */
  useEffect(() => {
    if (!testInfo) {
      return null;
    }

    if (fromCandidateProfile) {
      setTestInfo(allTests.get(testId));
    }

    initializeScoreRange();

    const hasIllustraitWorkStyles =
      testId === "48" &&
      testInfo.getIn(["report", "details", "hasWorkStyles"]) === 1;

    if (hasIllustraitWorkStyles || testId === "15" || testId === "24") {
      setShowReportNav(true);
      setTabToDisplay(testId);
    }
  }, []);

  /* ------------------------------------ Handlers -----------------------------------  */
  const handleReportNavTab = (testTab) => {
    setTabToDisplay(testTab);

    if (testTab === "workStyles") {
      trackDynamicScoreReport(
        EVENT_DYNAMIC_SCORE_REPORT_VIEW,
        null,
        null,
        companyAccountId,
        testEventId,
        "Report",
        testTab
      );

      if (!workStylesLoaded && !workStylesError) {
        onGetDynamicWorkStyles(testEventId, "html");
      }
    }

    if (testTab === testId) {
      setTestInfo(allTests.get(testId));
    } else if (testTab === "15") {
      setTestInfo(testInfo.getIn(["report", "csapData"]) || testInfo);
    } else if (testTab === "24") {
      setTestInfo(testInfo.getIn(["report", "salesApData"]) || testInfo);
    }
  };

  /* ------------------------------------ Helpers -----------------------------------  */
  const initializeScoreRange = () => {
    const hasScoreRange = testInfo.getIn(["baseline", "hasBaseline"]);
    const scoreRangeData = testInfo.get("rangeData");
    const reportInfo = testInfo.get("report");

    if (hasScoreRange) {
      const isDefaultScoreRange = testInfo.getIn([
        "baseline",
        "defaultBaseline",
      ]);

      const eppJobMatchName =
        (isDefaultScoreRange &&
          reportInfo.getIn(["eppData", "jobMatchScoreRange"])) ||
        null;

      const scoreRangeName = getScoreRangeName(
        scoreRangeData,
        testId,
        eppJobMatchName
      );

      const scoreRange = {
        rangeName: scoreRangeName,
        rangeMin: Number(scoreRangeData.get("range_low")),
        rangeMax: Number(scoreRangeData.get("range_high")),
        defaultTraitRanges:
          reportInfo.getIn(["eppData", "scoreRangeTraitRanges"]) || null,
      };

      setInitialScoreRange(scoreRange);

      // EPP score report will need the initial score range to be set to the job family
      setSelectedScoreRange(scoreRange);
    }
  };

  const getTestReportComponent = () => {
    if (!testInfo) {
      return null;
    }

    const scoreRangeData = testInfo.get("rangeData");
    const reportInfo = testInfo.get("report");
    const invalidTest = showInvalidTestWarning(Number(testId), reportInfo);

    return tabToDisplay === "workStyles" ? (
      <WorkStylesReport
        workStylesData={workStylesData}
        workStylesLoaded={workStylesLoaded}
        workStylesError={workStylesError}
      />
    ) : (
      renderTestReport(
        candidateInfo,
        initialScoreRange,
        invalidTest,
        reportInfo,
        scoreRangeData,
        tabToDisplay || testId,
        testInfo,
        hideBaseline,
        tabToDisplay && tabToDisplay !== testId,
        isInsightUser,
        isVistaUser,
        fromCandidateProfile,
        onCopyCandidateToJob,
        moveEmailCandidatesPermission,
        featureFlags,
        companyAccountId,
        pipelineStageName,
        isSampleJob,
        selectedScoreRange,
        setSelectedScoreRange,
        userData,
        hsVersion,
        onRedirectToDevelop
      )
    );
  };

  const getReportFooter = () => {
    if (!scheduledBy) {
      return null;
    }

    const testDate = `Test Date: ${format(
      convertDateTimezone(candidateInfo.get("dateEventTaken"), isAU),
      "MMM dd, yyyy"
    )}`;

    const eventId = `Event ID:
              ${addDashesToEventID(candidateInfo.get("eventId"))}`;

    const scheduledInfo = `Scheduled By: ${scheduledBy.get(
      "firstName"
    )} ${scheduledBy.get("lastName")}`;

    const testVersion = testId === "59" 
      ? "Test Ver: 1.0"
      : testInfo
        ? `Test Ver: ${testInfo.getIn(["test", "version"])}.0`
        : "";

    return (
      <Row className="mx-1 mt-2">
        <p>
          {testDate}
          {" | "}
          {eventId}
          {" | "}
          {scheduledInfo}
          {" | "}
          {testVersion}
        </p>
      </Row>
    );
  };

  const extendedNotice = (
    <Row className="mx-1 flex-nowrap">
      <img
        src={ExtendedBadge}
        alt="Extended time"
        height="30"
        className="mr-2"
      />
      <span style={{ color: "#78787b", fontSize: "11px" }}>
        In order to ensure a fair process, additional time was provided for this
        candidate to complete the assessment. This candidate's assessment
        results should be interpreted with reference to the job requirements and
        not relative to the results of other candidates, who may have completed
        the assessment with a different time limit.
      </span>
    </Row>
  );

  /* --------- Begin rendering logic ---------  */
  const isAU = candidateInfo && candidateInfo.get("locale") === "AU";
  const isExtended = testInfo.getIn(["report", "extended"]);

  const DownloadButton = renderDownloadButton(
    handleDownloadPdf,
    tabToDisplay || testId,
    "Report",
    false,
    testInfo.getIn(["test", "testName"])
  );

  return (
    <Container>
      {fromCandidateProfile ? (
        <>
          <TestInfoSection
            testInfo={testInfo}
            selectedScoreRange={selectedScoreRange}
          />
          {getTestReportComponent()}
          {getReportFooter()}
          {isExtended && extendedNotice}
        </>
      ) : (
        <>
          <Card className="px-2 pt-4">
            <Col className="pb-3">
              <Row className="d-flex justify-content-between mx-1">
                <Button
                  onClick={handleBack}
                  variant="link"
                  style={{
                    outline: "none",
                    color: "#425cc7",
                    fontSize: "14px",
                  }}
                  className="pt-1"
                >
                  <img src={arrowLeft} alt="Back to summary" />
                  <strong className="pl-2">Back to Summary</strong>
                </Button>
                {DownloadButton}
              </Row>
              {showReportNav && (
                <ScoreReportNav
                  handleReportNavTab={handleReportNavTab}
                  testId={testId}
                  tabToDisplay={tabToDisplay}
                />
              )}
              {tabToDisplay !== "workStyles" && (
                <TestInfoSection testInfo={testInfo} />
              )}
              {getTestReportComponent()}
              {getReportFooter()}
              {isExtended && extendedNotice}
            </Col>
          </Card>
          <Row className="d-flex justify-content-center mt-4">
            <p style={{ color: "#86888b" }}>
              © {new Date().getFullYear()} Criteria Corporation. All Rights
              Reserved. •{" "}
              <a
                href={
                  isAU
                    ? "mailto:au.support@criteriacorp.com"
                    : "https://www.criteriacorp.com"
                }
                target="_blank"
                style={{ textDecoration: "none", color: "inherit" }}
              >
                {isAU ? "au.support@criteriacorp.com" : "www.criteriacorp.com"}
              </a>{" "}
              • (877) 909-8378
            </p>
          </Row>
        </>
      )}
    </Container>
  );
};

ScoreReportPage.propTypes = {
  allTests: object,
  candidateInfo: object,
  companyAccountId: number,
  handleBack: func,
  handleDownloadPdf: func,
  scheduledBy: object,
  testEventId: string,
  testId: string,
  hideBaseline: bool,
  onGetDynamicWorkStyles: func,
  workStylesData: object,
  workStylesLoaded: bool,
  workStylesError: bool,
  isInsightUser: bool,
  isVistaUser: bool,
  fromCandidateProfile: bool,
  onCopyCandidateToJob: func,
  moveEmailCandidatesPermission: bool,
  featureFlags: object,
  pipelineStageName: oneOfType([string, bool]),
  isSampleJob: bool,
  userData: object,
  hsVersion: number,
  onRedirectToDevelop: func,
};

export default ScoreReportPage;
